import type { ListingsAPISponsoredContext } from '@noths/polaris-client-listings-api';
import { decideMiraklConsent, MIRAKL_PAGE } from '@noths/polaris-client-mirakl-tracking';

import { SponsoredListingsProducts } from 'src/featureFlags/featureFlags';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { getMiraklListingIdentity } from 'src/tracking/mirakl/getMiraklViewEvent';

/**
 * Batches (the first load plus "Load more" clicks) that request sponsored products.
 * Where they sit in a batch is set in the Mirakl Ads back office: each ad unit has a
 * start position and a size (four units of two at positions 3, 13, 25 and 37), and
 * listings-api places every unit at its own position.
 */
export const SPONSORED_MAX_BATCHES = 3;

/**
 * What the ad request needs to know about the visitor. Resolved from the request
 * cookies on the server and from `document.cookie` in the browser, by the same
 * consent rule the Mirakl tracking senders apply: without targeting consent the
 * user id is empty, and Mirakl counts the visitor anonymously.
 */
export interface SponsoredVisitor {
  /** From the forwarded-for chain on the server; empty in the browser. listings-api passes it to Mirakl only with consent. */
  ipAddress: string;
  /** The browser's user agent, on the server; empty in the browser, which sets its own. */
  userAgent: string;
  userConsent: boolean;
  userId: string;
}

/**
 * Whether this visitor gets sponsored products, from the Optimizely flag
 * `sponsored_listings_products` the edge worker assigns per request. Read from the
 * store, so the server render and "Load more" decide the same way. Off means no ad
 * headers, so listings-api never calls Mirakl, and no label, impression or click:
 * the page is the organic listing.
 */
export const isSponsoredProductsEnabled = (state: ReduxApplicationState): boolean =>
  state.user.featureFlags?.[SponsoredListingsProducts.NAME] === SponsoredListingsProducts.ON;

const parseCookies = (cookie: string): Partial<Record<string, string>> =>
  Object.fromEntries(
    cookie
      .split(';')
      .map((pair) => pair.trim().split('='))
      .filter(([name]) => name)
      .map(([name, ...value]) => [name, decodeURIComponent(value.join('='))]),
  );

/**
 * The browser's view of the visitor, for "Load more" requests that go straight to
 * listings-api. The `mvt-uid` cookie is read from `document.cookie`; if the edge
 * worker ever marks it HttpOnly the id is empty here, which Mirakl accepts.
 */
export const getBrowserSponsoredVisitor = (): SponsoredVisitor => {
  if (typeof document === 'undefined') {
    return { userConsent: false, userId: '', ipAddress: '', userAgent: '' };
  }
  const consent = decideMiraklConsent({
    cookies: parseCookies(document.cookie),
    ipAddress: '',
    isBot: false,
    referer: '',
    url: '',
    userAgent: '',
  });

  return { userConsent: consent.optedIn, userId: consent.userId, ipAddress: '', userAgent: '' };
};

/**
 * Product codes shown on the batches before the one being requested, sponsored or
 * organic, so listings-api never shows a product twice in the listing. The batch
 * being requested is left out, so a re-request of it keeps its own products. At
 * most two batches of 60, under 1 KB as a header.
 */
const shownProductCodes = (state: ReduxApplicationState, batch: number): number[] =>
  Object.entries(state.products.pages)
    .filter(([page]) => Number(page) < batch)
    .flatMap(([, products]) => products ?? [])
    .map((product) => product.code);

/**
 * The ad context for the batch about to be requested, or `undefined` when the
 * request must be organic only: the flag is off, the visitor is a robot, the page
 * has no identity Mirakl knows (partner pages), or the batch is past the cap.
 */
export const getSponsoredContext = (
  state: ReduxApplicationState,
  visitor: SponsoredVisitor,
): ListingsAPISponsoredContext | undefined => {
  if (!isSponsoredProductsEnabled(state) || state.user.bot) {
    return undefined;
  }
  const batch = state.productsQuery.page ?? 1;
  if (batch > SPONSORED_MAX_BATCHES) {
    return undefined;
  }
  const identity = getMiraklListingIdentity(state);
  if (!identity) {
    return undefined;
  }

  return {
    batch,
    ...(identity.pageId === MIRAKL_PAGE.PLP
      ? { categoryId: identity.categoryId }
      : { keywords: identity.keywords }),
    pageId: identity.pageId,
    shownProductCodes: shownProductCodes(state, batch),
    userConsent: visitor.userConsent,
    userId: visitor.userId,
    ipAddress: visitor.ipAddress,
    userAgent: visitor.userAgent,
  };
};
